"use client";

import { useCallback, useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import { Bot, X } from "lucide-react";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { AgentMessages } from "./AgentMessages";
import { AgentInput } from "./AgentInput";
import { MCPClient, AgentMessage } from "@/lib/agent/mcp-client";
import { useAgentStore } from "@/lib/stores/useAgentStore";

/**
 * AgentSidebar - Right-side panel for the Kreayt Agent
 *
 * Features:
 * - Slides in from right using Radix Sheet
 * - Maintains MCP connection across page navigations
 * - Persists chat history
 * - Handles navigation and highlighting after commands
 */
export function AgentSidebar() {
  const { data: session } = useSession();
  const router = useRouter();
  const mcpClientRef = useRef<MCPClient | null>(null);

  const {
    isSidebarOpen,
    closeSidebar,
    messages,
    addMessage,
    isConnected,
    setIsConnected,
    isLoading,
    setIsLoading,
    pendingNavigation,
    clearPendingNavigation,
    addHighlightedItem,
  } = useAgentStore();

  /**
   * Parse agent response for navigation actions
   * Looks for patterns like "navigate:quotes" or structured data with navigation info
   */
  const handleNavigationFromMessage = useCallback((message: AgentMessage) => {
    if (message.role !== 'assistant') return;

    // Check for structured navigation data
    if (message.data?.navigation) {
      const nav = message.data.navigation;
      useAgentStore.getState().setPendingNavigation({
        path: nav.path,
        highlightIds: nav.highlightIds,
        highlightType: nav.highlightType,
      });
      return;
    }

    // Check content for navigation keywords
    const content = message.content.toLowerCase();

    // Map of action keywords to routes
    // Note: /streams is the working route; /stream/[id] requires streamId from navigation data
    const navigationMap: Record<string, string> = {
      'approved quotes': '/streams',
      'pending quotes': '/streams',
      'your quotes': '/streams',
      'generated quotes': '/streams',
      'streams list': '/streams',
      'your streams': '/streams',
      'videos list': '/streams',
      'your videos': '/streams',
      'credit balance': '/streams',
    };

    // Only navigate if the message indicates a successful action was taken
    const actionIndicators = ['approved', 'generated', 'created', 'updated', 'assigned'];
    const hasAction = actionIndicators.some(indicator => content.includes(indicator));

    if (hasAction) {
      for (const [keyword, path] of Object.entries(navigationMap)) {
        if (content.includes(keyword.toLowerCase())) {
          useAgentStore.getState().setPendingNavigation({ path });
          return;
        }
      }
    }
  }, []);

  // Initialize MCP client when sidebar opens and user is authenticated
  useEffect(() => {
    if (!session?.idToken || !isSidebarOpen) return;

    // Don't create new client if one already exists
    if (mcpClientRef.current) return;

    const client = new MCPClient({
      onMessage: (message) => {
        addMessage(message);
        setIsLoading(false);

        // Check for navigation actions in the message
        handleNavigationFromMessage(message);
      },
      onError: (error) => {
        console.error("MCP Client error:", error);
        toast.error(error.message || "Connection error");
        setIsLoading(false);
      },
      onConnected: () => {
        setIsConnected(true);
        console.log("Agent sidebar connected");
      },
      onDisconnected: () => {
        setIsConnected(false);
        console.log("Agent sidebar disconnected");
      },
    });

    mcpClientRef.current = client;
    client.connect();

    // Only cleanup when sidebar closes (not on every re-render)
    // We intentionally don't return a cleanup function here because
    // we want the connection to persist while the sidebar is open
  }, [session?.idToken, isSidebarOpen, addMessage, setIsConnected, setIsLoading, handleNavigationFromMessage]);

  // Separate cleanup effect - only runs when sidebar closes
  useEffect(() => {
    if (!isSidebarOpen && mcpClientRef.current) {
      mcpClientRef.current.disconnect();
      mcpClientRef.current = null;
    }
  }, [isSidebarOpen]);

  // Handle pending navigation (navigate to page and highlight items)
  useEffect(() => {
    if (pendingNavigation) {
      // Navigate to the target page
      router.push(pendingNavigation.path);

      // Add items to highlight
      if (pendingNavigation.highlightIds) {
        pendingNavigation.highlightIds.forEach((id) => {
          addHighlightedItem(id);
        });

        // Clear highlights after animation duration
        const HIGHLIGHT_DURATION_MS = 3000;
        setTimeout(() => {
          useAgentStore.getState().clearHighlightedItems();
        }, HIGHLIGHT_DURATION_MS);
      }

      clearPendingNavigation();
    }
  }, [pendingNavigation, router, addHighlightedItem, clearPendingNavigation]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!mcpClientRef.current || !isConnected) {
        toast.error("Not connected to agent");
        return;
      }

      try {
        setIsLoading(true);

        // Add user message immediately
        const userMessage: AgentMessage = {
          id: Date.now().toString(),
          role: "user",
          content,
          timestamp: new Date(),
        };
        addMessage(userMessage);

        // Send to MCP server
        await mcpClientRef.current.sendMessage(content);
      } catch (error) {
        console.error("Error sending message:", error);
        toast.error("Failed to send message");
        setIsLoading(false);
      }
    },
    [isConnected, addMessage, setIsLoading]
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeSidebar();
    }
  };

  return (
    <Sheet open={isSidebarOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[400px] sm:max-w-[400px] p-0 flex flex-col bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900 border-l border-gray-800"
        hideCloseButton
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <SheetTitle className="text-lg font-semibold text-white">
                  Kreayt Agent
                </SheetTitle>
                <SheetDescription className="text-xs text-gray-400">
                  {isConnected ? "Connected" : "Connecting..."}
                </SheetDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeSidebar}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </SheetHeader>

        {/* Messages Area */}
        <div className="flex-1 overflow-hidden">
          <AgentMessages messages={messages} />
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0">
          <AgentInput
            onSend={handleSendMessage}
            disabled={!isConnected || isLoading}
          />
        </div>

        {/* Connection Status Banner */}
        {!isConnected && (
          <div className="absolute top-16 left-1/2 transform -translate-x-1/2 bg-yellow-500/90 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
            Connecting to agent...
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
